import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useCallback, useState } from "react";
import { WishlistBrandCollection } from "src/types/graphql.d";
import { ApolloError, ApolloQueryResult } from "@apollo/client";
import { useAuth } from "src/hooks/useAuth";

export const WISHLIST_BRANDS = gql`
  query wishlistBrands($limit: Int!, $offset: Int!) {
    wishlistBrands(limit: $limit, offset: $offset, sort: CREATED_DATE_DESC) {
      total
      items {
        id
        name
        discount
        subtitle
        genders
        logo
        slug
        overlay_logo
        logo_svg
        is_coming_soon
        image
        cover_image
        labels {
          color
          priority
          value
          text_color
          background_color
        }
        designer_brand
        user_wishlist_item_id
        created_at
      }
    }
  }
`;

interface wishlistBrandData {
  wishlistBrands: WishlistBrandCollection | undefined;
}
interface wishlistBrandVars {
  limit: number;
  offset: number;
}
interface WishlistBrandsHook {
  data: wishlistBrandData | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  hasMore: boolean;
  loadMore: () => Promise<ApolloQueryResult<wishlistBrandData>>;
  page: number;
}
const useWishListBrands = (
  limit: number,
  offset: number,
  skip?: boolean
): WishlistBrandsHook => {
  const { accessToken } = useAuth();

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const { loading, error, data, fetchMore } = useQuery<
    wishlistBrandData,
    wishlistBrandVars
  >(WISHLIST_BRANDS, {
    variables: { limit, offset },
    context: { token: accessToken || "" },
    ssr: false,
    fetchPolicy: "cache-and-network",
    skip,
  });

  const loadMore = useCallback(() => {
    return fetchMore({
      variables: {
        limit,
        offset: (page + 1) * limit,
      },
      // @ts-ignore
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const { wishlistBrands } = fetchMoreResult;

        if (wishlistBrands?.items.length === 0) {
          setHasMore(false);
        }
        setPage(page + 1);
        const newItems = [
          ...(prev?.wishlistBrands?.items || []),
          ...(wishlistBrands?.items.filter(
            (n) => !prev?.wishlistBrands?.items?.some((p) => p?.id === n?.id)
          ) || []),
        ];

        return {
          wishlistBrands: {
            total: wishlistBrands?.total,
            items: newItems,
            __typename: prev?.wishlistBrands?.__typename, // eslint-disable-line no-underscore-dangle
          },
        };
      },
    });
  }, [fetchMore, limit, page, setHasMore]);

  return { loading, error, data, loadMore, hasMore, page };
};

export { useWishListBrands };
